import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  CircularProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
  Alert
} from '@mui/material';
import { ArrowBack, Download, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import { useGetlowerhierarchyDataMutation } from 'state/api';

const SrSm = () => {
  const { MgCd } = useSelector((state) => state.global || {});
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fetchData, { data, isLoading, isError, error }] = useGetlowerhierarchyDataMutation();

  useEffect(() => {
    if (MgCd) {
      console.log("Fetching with:", { MgCd });
      fetchData({ MgCd }); // Send MgCd key exactly as backend expects
    }
  }, [MgCd, fetchData]);

  const refetch = () => {
    if (MgCd) {
      fetchData({ MgCd });
    }
  };

  // Extract actual array from response
  const actualData = data?.data || [];

  // Get dynamic columns based on naming patterns
  const dynamicColumns = actualData.length > 0
    ? Object.keys(actualData[0]).filter(key =>
        key.toLowerCase().includes('_approved') ||
        key.toLowerCase().includes('_rejected') ||
        key.toLowerCase().includes('_pending'))
    : [];

  const exportToExcel = () => {
    if (!actualData || actualData.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(actualData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MR_Scoring');
    XLSX.writeFile(wb, 'MrScoringData.xlsx');
  };

  return (
    <Box p={isMobile ? 1 : 3}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Tooltip title="Go Back">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item>
          <Typography variant="h5" fontWeight="bold">
            MR Today Details
          </Typography>
        </Grid>
        <Grid item>
          <Tooltip title="Refresh">
            <IconButton onClick={refetch} disabled={!MgCd || isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton 
              onClick={exportToExcel} 
              disabled={!actualData || actualData.length === 0}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {!MgCd ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          User authentication data not available. Please log in again.
        </Alert>
      ) : isLoading ? (
        <Box textAlign="center" mt={4}>
          <CircularProgress />
          <Typography variant="body1" mt={2}>Loading data...</Typography>
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.data?.message || 'Failed to load data'}
        </Alert>
      ) : !actualData || actualData.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No data available for this user
        </Alert>
      ) : (
        <Paper elevation={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>MR Name</strong></TableCell>
                  <TableCell><strong>HeadQrt</strong></TableCell>
                  {dynamicColumns.map(col => (
                    <TableCell key={col}><strong>{col.replaceAll('_', ' ')}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {actualData.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.MrName}</TableCell>
                    <TableCell>{row.HeadQrt}</TableCell>
                    {dynamicColumns.map(col => (
                      <TableCell key={col}>{row[col]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default SrSm;
